import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import Downshift from 'downshift';
import { debounce } from 'lodash';

import { ContactEntity } from '~/components/Contact';
import { FsButton, FsMenuItem, PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import ListItemIcon from '~/components/core/Atomic/ListItemIcon';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { ContactIcon } from '~/components/icons';
import Divider from '~/components/ui/atoms/Divider';
import cn from '~/Utils/cn';

const DEBOUNCE_TIME = 500;

const searchResultsBaseClassName = 'overflow-hidden rounded-md bg-white shadow-elevation-8 z-[100]';

// Fixed results escape a container that clips them, such as a popover. Floating UI places them under
// the field even when an ancestor has a transform, which makes the ancestor the box `fixed` is relative to.
const FixedSearchResults = ({ anchorEl, children }) => {
  const { refs, floatingStyles, isPositioned } = useFloating({
    // The results mount only while open. `open` lets Floating UI report `isPositioned`.
    open: true,
    strategy: 'fixed',
    placement: 'bottom-start',
    elements: { reference: anchorEl },
    whileElementsMounted: autoUpdate,
  });

  return (
    // Hidden until the first position is computed, so the results never paint at the 0,0 starting point.
    <div
      ref={refs.setFloating}
      className={cn(searchResultsBaseClassName, { invisible: !isPositioned })}
      style={floatingStyles}
    >
      {children}
    </div>
  );
};

FixedSearchResults.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  children: PropTypes.node,
};

export class ContactSearch extends Component {
  constructor(props) {
    super(props);
    this.searchTextFieldEl = React.createRef();
    this.searchTextFieldInputEl = React.createRef();
    this.state = { inputCopy: '' }; // hack for onFocus of existing contact
  }

  render() {
    const {
      textInputRef,
      contactsList,
      contactSelected,
      isSearchInProgress,
      onSearchValueChange,
      onContactSelect,
      onContactUpdate,
      disallowNew,
      TextFieldProps,
      fixedSearchResults,
      initialSelectedContact,
      RemoveOutOfClaimDivider,
      organizationContactRolesDict,
      disableAdvanceSearch,
      handleAdvancedSearch,
      shouldShowAllOption,
      onShowAllContacts,
    } = this.props;

    const { inputCopy } = this.state;

    const MAX_CONTACT_TO_DISPLAY = 5;
    const contactsListTruncated = contactsList.slice(0, MAX_CONTACT_TO_DISPLAY);
    const firstContactFetchedIdx = contactsListTruncated.findIndex((contact) => contact.is_fetched);

    const SearchResults = fixedSearchResults ? FixedSearchResults : 'div';
    const searchResultsProps = fixedSearchResults
      ? { anchorEl: this.searchTextFieldEl.current }
      : {
          className: cn(searchResultsBaseClassName, 'absolute'),
        };

    if (initialSelectedContact) {
      return (
        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            disabled
            value={initialSelectedContact.selectedContactDisplayName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ContactIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
      );
    }

    return (
      <>
        <Downshift
          onChange={(selection, stateAndHelpers) => onContactSelect(selection, stateAndHelpers)}
          onStateChange={(stateChanges) => {
            if (stateChanges.selectedItem) {
              if (stateChanges.type === Downshift.stateChangeTypes.clickItem) {
                // to avoid loss of orientation for keyboard users, blur input only if selection was made via click
                this.searchTextFieldInputEl.current?.blur();
              } else if (document.activeElement === this.searchTextFieldInputEl.current) {
                this.searchTextFieldInputEl.current?.select();
                // (backspace will delete the entire input as long as the text selection holds)
              }
            }
          }}
          itemToString={(item) => (item ? item.full_name : '')}
          onInputValueChange={debounce((newInputValue) => {
            onSearchValueChange(newInputValue);
            this.setState({ inputCopy: newInputValue });
          }, DEBOUNCE_TIME)}
          selectedItem={contactSelected}
        >
          {({
            getInputProps,
            getItemProps,
            getLabelProps,
            isOpen,
            openMenu,
            inputValue,
            highlightedIndex,
            selectedItem,
            clearSelection,
          }) => (
            <div className="group relative">
              <div style={{ display: 'flex' }}>
                <TextField
                  ref={(el) => (this.searchTextFieldEl.current = el)}
                  inputRef={(el) => {
                    this.searchTextFieldInputEl.current = el;
                    if (textInputRef) {
                      textInputRef.current = el;
                    }
                  }}
                  fullWidth
                  InputLabelProps={{ ...getLabelProps() }}
                  InputProps={{
                    ...getInputProps({
                      placeholder: TextFieldProps && TextFieldProps.label ? '' : 'Search Contact',
                      startAdornment: (
                        <InputAdornment position="start">
                          {selectedItem && selectedItem.id !== 'new' && selectedItem.full_name === inputValue ? (
                            <ContactEntity
                              contactId={selectedItem.id}
                              contactDisplayName={selectedItem.full_name}
                              hideDisplayName
                              onContactUpdate={onContactUpdate}
                            />
                          ) : (
                            <ContactIcon />
                          )}
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            className={cn('invisible p-4 focus-visible:visible', {
                              'group-focus-within:visible group-hover:visible':
                                selectedItem && !(TextFieldProps && TextFieldProps.disabled),
                            })}
                            title="Clear"
                            onClick={() => clearSelection()}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          {!disableAdvanceSearch && (
                            <IconButton
                              style={{
                                padding: 4,
                                visibility: TextFieldProps?.disabled ? 'hidden' : 'visible',
                              }}
                              title="Advanced Search"
                              onClick={() => handleAdvancedSearch(inputCopy)}
                            >
                              <Tooltip title="Advanced Search">
                                <PersonSearchIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                      onFocus: (e) => {
                        if (selectedItem) {
                          e.target.select();
                          this.setState({ inputCopy: '' }); // so that "New Contact" will be of empty name
                          openMenu();
                          onSearchValueChange(' ');
                        }
                      },
                      onBlur: () => {
                        if (inputValue === '') clearSelection();
                      },
                    }),
                  }}
                  {...TextFieldProps}
                />
              </div>

              {isOpen ? (
                <SearchResults {...searchResultsProps}>
                  {contactsListTruncated.map((contact, idx) => (
                    <Fragment key={idx}>
                      {idx === firstContactFetchedIdx && !RemoveOutOfClaimDivider && (
                        <>
                          <Divider />
                          <Typography
                            style={{ margin: '5px 0 0 16px' }}
                            color="textSecondary"
                            display="block"
                            variant="caption"
                          >
                            Not related to claim
                          </Typography>
                        </>
                      )}
                      <MenuItem
                        {...getItemProps({ item: contact, key: contact.id })}
                        selected={highlightedIndex === idx}
                        component="div"
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', wrap: 'noWrap' }}>
                          <ListItemIcon>
                            <ContactIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <span>
                                {contact.full_name}
                                <em>{` - ${organizationContactRolesDict[contact.role]['desc']}`}</em>
                              </span>
                            }
                            secondary={<span>{contact.full_address}</span>}
                          />
                        </span>
                      </MenuItem>
                    </Fragment>
                  ))}
                  {!disallowNew && (
                    <RestrictedPermissions action={PERMISSION_ACTIONS.CONTACT} verb={PERMISSION_VERBS.WRITE}>
                      <FsMenuItem
                        {...getItemProps({ item: { id: 'new', full_name: inputCopy } })}
                        component="div"
                        disabled={isSearchInProgress}
                      >
                        {`${inputCopy} (Create new contact)`}
                      </FsMenuItem>
                    </RestrictedPermissions>
                  )}
                  {shouldShowAllOption ? (
                    <FsMenuItem component="div" disabled={isSearchInProgress}>
                      <FsButton
                        color="primary"
                        onClick={() => {
                          onShowAllContacts(inputCopy);
                          clearSelection();
                        }}
                        className="w-full justify-center"
                      >
                        show all contacts
                      </FsButton>
                    </FsMenuItem>
                  ) : null}
                </SearchResults>
              ) : null}
            </div>
          )}
        </Downshift>
      </>
    );
  }
}

ContactSearch.propTypes = {
  textInputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  initialSelectedContact: PropTypes.object,
  contactSelected: PropTypes.object,
  isSearchInProgress: PropTypes.bool,
  onSearchValueChange: PropTypes.func.isRequired,
  onContactSelect: PropTypes.func.isRequired,
  contactsList: PropTypes.array,
  contact: PropTypes.object,
  isAddingContact: PropTypes.bool,
  disallowNew: PropTypes.bool,
  fixedSearchResults: PropTypes.bool,
  TextFieldProps: PropTypes.object,
  onContactUpdate: PropTypes.func,
  RemoveOutOfClaimDivider: PropTypes.bool,
  organizationContactRolesDict: PropTypes.object.isRequired,
  disableAdvanceSearch: PropTypes.bool,
  handleAdvancedSearch: PropTypes.func,
  shouldShowAllOption: PropTypes.bool,
  onShowAllContacts: PropTypes.func,
};
